type TimestampLike = { toDate: () => Date };

function isTimestampLike(value: unknown): value is TimestampLike {
  return (
    typeof value === 'object' &&
    value !== null &&
    'toDate' in value &&
    typeof (value as TimestampLike).toDate === 'function'
  );
}

export function toTimestamp(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (value instanceof Date) return value.getTime();
  if (isTimestampLike(value)) return value.toDate().getTime();
  return 0;
}

export function compareTimestamps(a: unknown, b: unknown): number {
  return toTimestamp(b) - toTimestamp(a);
}

export function formatRelativeTimestamp(
  iso: string | undefined,
  {
    includeTime = false,
    yesterdayLabel = 'Yesterday',
  }: { includeTime?: boolean; yesterdayLabel?: string } = {}
): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOf(now) - startOf(date)) / (1000 * 60 * 60 * 24));

  const timeString = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (dayDiff === 0) return timeString;
  if (dayDiff === 1) return includeTime ? `${yesterdayLabel}, ${timeString}` : yesterdayLabel;

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  if (date.getFullYear() !== now.getFullYear()) {
    dateOptions.year = 'numeric';
  }

  const dateString = date.toLocaleDateString(undefined, dateOptions);
  return includeTime ? `${dateString}, ${timeString}` : dateString;
}
